import React, { useCallback, useMemo } from 'react';
import classNames from 'classnames';
import PropTypes from 'prop-types';

import { QuickBonus, Slider } from '@atlas/components';
import { AccountManager } from '@atlas/core';
import { Icon, Label, ToggleCheckbox } from '@atlas/ui';

import { CDN_TYPE_CRM, imageUrlCDN } from 'common/utils/utils';
import { BASE_PATH, QUICK_DEPOSIT_POPUP_TITLE_TYPE } from 'src/common/constants';
import useBonusSliderAtEnd from 'src/hooks/useBonusSliderAtEnd';
import useLanguage from 'src/hooks/useLanguage';

const TITLE = {
  [QUICK_DEPOSIT_POPUP_TITLE_TYPE.INSUFFICIENT]: 'insufficient_funds_get_bonus',
  [QUICK_DEPOSIT_POPUP_TITLE_TYPE.QUICK_DEPOSIT]: 'quick_deposit_get_bonus',
};

const QuickDepositBonus = ({
  bonuses,
  isBonusesDisabled,
  onSelectBonus,
  selectedBonus,
  titleType = QUICK_DEPOSIT_POPUP_TITLE_TYPE.QUICK_DEPOSIT,
}) => {
  const currency = AccountManager.getAccountCurrency();
  const { atEnd: bonusSliderAtEnd, onSwiper: onBonusSwiper } = useBonusSliderAtEnd();
  const { alpha3Code } = useLanguage();
  const bonusList = useMemo(() => (bonuses ? [bonuses].flat() : []), [bonuses]);
  const sliderKey = useMemo(() => bonusList.map(({ id }) => id).join('-'), [bonusList]);
  const hasSingleBonus = bonusList.length === 1;

  const handleSelectBonus = useCallback(
    bonus => {
      const isActive = selectedBonus?.id === bonus.id;
      const newActiveBonus = isActive ? null : bonus;

      onSelectBonus(newActiveBonus);
    },
    [selectedBonus, onSelectBonus],
  );

  const sliderConfig = useMemo(
    () => ({
      autoHeight: false,
      autoplay: false,
      loop: false,
      nextBtn: <Slider.NextBtn>next</Slider.NextBtn>,
      prevBtn: <Slider.PrevBtn>prev</Slider.PrevBtn>,
      slidesPerView: 'auto',
      spaceBetween: '8px',
    }),
    [],
  );

  if (!bonusList.length) {
    return null;
  }

  const renderBonus = bonus => {
    const finance = bonus.financeDataList.find(item => item.currency === currency);
    const bonusImage = bonus?.localizedImageInfoList?.find(
      item => item.language.toLowerCase() === alpha3Code.toLowerCase(),
    );

    const imageLink = bonusImage?.imageLink
      ? `${imageUrlCDN(bonusImage.imageLink, CDN_TYPE_CRM)}`
      : `${BASE_PATH}/static/assets/images/gift.svg`;

    const isActive = selectedBonus?.id === bonus.id;

    const bonusTitle = <h4 className="quick-deposit-bonus-title">{bonus.name}</h4>;

    const bonusDescription = (
      <div className="quick-deposit-bonus-description">
        <Label
          message="deposit_min_amount"
          params={{
            currency: bonus.currency,
            minBonusAmount: finance?.minDeposit,
          }}
        />
      </div>
    );

    const disableBlock = (
      <div className="bonus-disable quick-deposit-disable">
        <Icon className="disabled-icon" name="lock" />
        <Label message="deposit_bonus_unavaliable_message" />
      </div>
    );

    return (
      <div
        className={classNames('quick-deposit-bonus', {
          ['quick-deposit-bonus--single']: hasSingleBonus,
        })}
        key={bonus.id}
      >
        <QuickBonus
          active={isActive}
          description={bonusDescription}
          disabled={bonus.disabled || false}
          disabledBlock={disableBlock}
          image={imageLink}
          title={bonusTitle}
          toggleCheckbox={
            <ToggleCheckbox
              checked={isActive}
              disabled={isBonusesDisabled}
              onChange={() => handleSelectBonus(bonus)}
            />
          }
        />
      </div>
    );
  };

  if (hasSingleBonus) {
    return renderBonus(bonusList[0]);
  }

  return (
    <div
      className={classNames('quick-deposit-wrapper', {
        single: hasSingleBonus,
      })}
    >
      {!hasSingleBonus && <Label className="slider-title" message={TITLE[titleType]} />}
      <div
        className={classNames('bonus-slider-outer', {
          'bonus-slider-outer--at-end': bonusSliderAtEnd,
        })}
      >
        <Slider key={sliderKey} {...sliderConfig} onSwiper={onBonusSwiper}>
          {bonusList.map(bonus => (
            <Slider.Slide className="quick-deposit-slide" key={bonus.id}>
              {renderBonus(bonus)}
            </Slider.Slide>
          ))}
        </Slider>
      </div>
    </div>
  );
};

const BonusShape = PropTypes.shape({
  currency: PropTypes.string,
  disabled: PropTypes.bool,
  financeDataList: PropTypes.arrayOf(
    PropTypes.shape({
      currency: PropTypes.string.isRequired,
      minDeposit: PropTypes.number.isRequired,
    }),
  ),
  id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  image: PropTypes.string,
  name: PropTypes.string.isRequired,
});

QuickDepositBonus.propTypes = {
  bonuses: PropTypes.oneOfType([BonusShape, PropTypes.arrayOf(BonusShape)]),
  isBonusesDisabled: PropTypes.bool,
  onSelectBonus: PropTypes.func.isRequired,
  selectedBonus: BonusShape,
  titleType: PropTypes.oneOfType([
    QUICK_DEPOSIT_POPUP_TITLE_TYPE.QUICK_DEPOSIT,
    QUICK_DEPOSIT_POPUP_TITLE_TYPE.INSUFFICIENT,
  ]),
};

export default QuickDepositBonus;
